import { useEffect } from "react";
import { BASE_URL } from "@/lib/siteMap";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href, hreflang) {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function SeoHead({ lang, title, desc, pathEs, pathEn, schemas = [], image, noindex = false }) {
  useEffect(() => {
    const self = lang === "es" ? pathEs : pathEn;
    const url = `${BASE_URL}${self}`;
    document.title = title;
    document.documentElement.lang = lang === "es" ? "es-CL" : "en";

    upsertMeta("name", "description", desc);
    if (noindex) upsertMeta("name", "robots", "noindex, nofollow");
    else document.head.querySelector('meta[name="robots"]')?.remove();
    upsertLink("canonical", url);
    upsertLink("alternate", `${BASE_URL}${pathEs}`, "es-CL");
    upsertLink("alternate", `${BASE_URL}${pathEn}`, "en");
    upsertLink("alternate", `${BASE_URL}${pathEs}`, "x-default");

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:locale", lang === "es" ? "es_CL" : "en_US");
    upsertMeta("property", "og:locale:alternate", lang === "es" ? "en_US" : "es_CL");
    if (image) upsertMeta("property", "og:image", image);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", desc);

    document.querySelectorAll("script[data-seo-jsonld]").forEach((n) => n.remove());
    schemas.forEach((schema) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "true");
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
    });
  }, [lang, title, desc, pathEs, pathEn, schemas, image, noindex]);

  return null;
}

export function orgSchema(lang) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "DEVOLUZION",
    url: BASE_URL,
    logo: `${BASE_URL}/assets/icon-512.png`,
    description:
      lang === "es"
        ? "Empresa de evolución digital: diseño web, software, ecommerce, automatización, inteligencia artificial y growth."
        : "Digital evolution company: web design, software, ecommerce, automation, artificial intelligence and growth.",
    email: "contacto@devoluzion.com",
    telephone: "+56935609185",
    address: [
      { "@type": "PostalAddress", addressLocality: "La Serena", addressCountry: "CL" },
      { "@type": "PostalAddress", addressLocality: "Santiago", addressCountry: "CL" },
      { "@type": "PostalAddress", addressLocality: "Concepción", addressCountry: "CL" },
    ],
    sameAs: [
      "https://www.instagram.com/devoluzion/",
      "https://www.facebook.com/profile.php?id=61592628959822",
      "https://www.linkedin.com/company/dev-inc-latam/",
    ],
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${BASE_URL}${it.path}`,
    })),
  };
}

export function serviceSchema(lang, name, desc, path) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description: desc,
    url: `${BASE_URL}${path}`,
    provider: { "@type": "Organization", name: "DEVOLUZION", url: BASE_URL },
    areaServed: lang === "es" ? { "@type": "Country", name: "Chile" } : "Worldwide",
    inLanguage: lang === "es" ? "es-CL" : "en",
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function articleSchema(lang, article, path) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.desc,
    inLanguage: lang === "es" ? "es-CL" : "en",
    author: { "@type": "Organization", name: "DEVOLUZION", url: BASE_URL },
    publisher: { "@type": "Organization", name: "DEVOLUZION", logo: { "@type": "ImageObject", url: `${BASE_URL}/assets/icon-512.png` } },
    mainEntityOfPage: `${BASE_URL}${path}`,
    datePublished: article.date,
  };
}
